/**
 * Batch + batch-item status presentation (§12/§15/§40 — Pack-workflow Tasks 2-3).
 * Maps BatchStatus / BatchItemStatus to a status tone (reusing the order palette)
 * and a human label, so the Batches list + detail are coloured consistently with
 * the rest of the console. Pure + DOM-free → unit-tested.
 */
import type { BatchStatus, BatchItemStatus, FulfilmentMode } from "@cairn/shared";
import type { StatusTone } from "./status.js";

export const BATCH_STATUS_TONE: Record<BatchStatus, StatusTone> = {
  DRAFT: "new",
  PROCESSING: "routed",
  PARTIAL: "hold",
  COMPLETE: "done",
  FAILED: "exception",
};

export const BATCH_STATUS_LABEL: Record<BatchStatus, string> = {
  DRAFT: "Draft",
  PROCESSING: "Processing",
  PARTIAL: "Partial",
  COMPLETE: "Complete",
  FAILED: "Failed",
};

export const BATCH_ITEM_TONE: Record<BatchItemStatus, StatusTone> = {
  PENDING: "new",
  LABEL_CREATED: "routed",
  PRINTED: "routed",
  PACKED: "ready",
  FAILED: "exception",
  SKIPPED: "cancelled",
  VOIDED: "cancelled",
};

export const BATCH_ITEM_LABEL: Record<BatchItemStatus, string> = {
  PENDING: "Pending",
  LABEL_CREATED: "Label created",
  PRINTED: "Printed",
  PACKED: "Packed",
  FAILED: "Failed",
  SKIPPED: "Skipped",
  VOIDED: "Voided",
};

export function batchStatusTone(s: BatchStatus): StatusTone {
  return BATCH_STATUS_TONE[s] ?? "new";
}
export function batchStatusLabel(s: BatchStatus): string {
  return BATCH_STATUS_LABEL[s] ?? s;
}
export function batchItemTone(s: BatchItemStatus): StatusTone {
  return BATCH_ITEM_TONE[s] ?? "new";
}
export function batchItemLabel(s: BatchItemStatus): string {
  return BATCH_ITEM_LABEL[s] ?? s;
}

export function modeLabel(mode: FulfilmentMode): string {
  return mode === "LABEL_FIRST" ? "Label-first" : "Pack-first";
}

/** One-line description of what a mode means at batch time (dialog + detail). */
export function modeHint(mode: FulfilmentMode): string {
  return mode === "LABEL_FIRST"
    ? "All labels are created up front (theoretical weight); the packer matches each pre-printed label at the bench."
    : "The label is created for each order at the bench, one by one, after the weight passes.";
}
